/**
 * הגדרות — "לראות שהחיבורים חיים, לתקן הגדרת מס, ולהחליף ערכת נושא" (spec-business §4.7).
 *
 * Desktop: a vertical tab list on the RIGHT (the RTL start side) with the panel beside it;
 * mobile: the same five sections as accordions (theme.css switches at 720px).
 * Secrets (HYP key, LabTop token) are write-only — the API returns a hint, never the value.
 */

import { Fragment, useEffect, useState } from 'react';
import type { AlertType, SettingsResponse, SummaryResponse, TaxSettings } from '../../../api-types.ts';
import { fmtDate, fmtDateTime, fmtMoney, mask } from '../format.ts';
import { ACCOUNT_TYPE, CONNECTION_STATUS, he, providerName } from '../i18n/he.ts';
import type { AlertRuleValues } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { ProviderMark } from '../../../../shared/ui/src/index.tsx';
import { Card, Chip, Mask, Money, Segmented, Skeleton, Switch } from '../components/ui.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';
import { useRouter } from '../hooks/useRouter.tsx';
import { buildPath, settingsTab, SETTINGS_TABS, type SettingsTab } from '../router.ts';
import { errorMessage } from '../api-select.ts';
import { useRefreshDialog } from '../components/Shell.tsx';

const TAB_ICONS: Record<SettingsTab, string> = {
  connections: 'plug',
  tax: 'landmark',
  alerts: 'bell',
  display: 'sun',
  account: 'user',
};

/** The alert rules the settings screen exposes, in the spec's order (§4.7 "התראות"). */
const ALERT_ROWS: AlertType[] = [
  'INVOICE_OVERDUE',
  'VAT_DUE',
  'TAX_ADVANCE_DUE',
  'CARD_CHARGE_SOON',
  'CATEGORY_OVER_BASELINE',
  'RECEIPT_UNMATCHED',
  'EXPENSE_NO_INVOICE',
  'FORECAST_NEGATIVE',
];

export function SettingsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, appearance, setAppearance, toast, openDialog, user } = useApp();
  const { route, navigate } = useRouter();
  const active = settingsTab(route);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ connections: true });
  const settings = useQuery<SettingsResponse>(cacheKey('settings'), () => api.settings(), { scopes: ['settings'] });
  const accounts = useQuery(cacheKey('accounts'), () => api.accounts(), { scopes: ['accounts'] });
  const refresh = useRefreshDialog(summary);
  const data = settings.data;

  const [tax, setTax] = useState<TaxSettings | null>(null);
  useEffect(() => {
    if (data && !tax) setTax(data.tax);
  }, [data, tax]);

  if (settings.loading && !data) {
    return (
      <div className="grid">
        <Card span={12}>
          <Skeleton className="ch" />
        </Card>
      </div>
    );
  }

  const ruleValues: AlertRuleValues = {
    invoiceOverdueDays: data?.alerts.invoiceOverdueDays ?? 1,
    invoiceOverdueRepeatDays: data?.alerts.invoiceOverdueRepeatDays ?? 14,
    vatWarnDays: data?.alerts.vatWarnDays ?? 14,
    vatCriticalDays: data?.alerts.vatCriticalDays ?? 3,
    taxAdvanceDays: data?.alerts.taxAdvanceDays ?? 10,
    cardChargeDays: data?.alerts.cardChargeDays ?? 2,
    categoryThresholdPct: data?.alerts.categoryThresholdPct ?? 20,
    categoryMinDayOfMonth: data?.alerts.categoryMinDayOfMonth ?? 7,
    receiptUnmatchedDays: data?.alerts.receiptUnmatchedDays ?? 3,
    staleHours: data?.alerts.staleHours ?? 36,
    floorLabel: fmtMoney(data?.tax.forecastFloorAgorot ?? 0),
  };

  const section = (key: SettingsTab, title: string, body: React.ReactNode) => (
    <section
      className={`card sec${active === key ? ' active' : ''}${openSections[key] ? ' open' : ''}`}
      id={`sec-${key}`}
      role="tabpanel"
      aria-labelledby={`tab-${key}`}
      key={key}
    >
      <button
        type="button"
        className="acc-h"
        aria-expanded={Boolean(openSections[key])}
        aria-controls={`sec-${key}`}
        onClick={() => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))}
      >
        <span>{title}</span>
        <Icon name="chevDown" />
      </button>
      <div className="sec-body">
        <h2 className="desk-only">{title}</h2>
        {body}
      </div>
    </section>
  );

  const saveTax = () => {
    if (!tax) return;
    api.updateTaxSettings({
      vatFrequency: tax.vatFrequency,
      vatBasis: tax.vatBasis,
      reportDay: tax.reportDay,
      advanceRate: tax.advanceRate,
      niMonthlyAgorot: tax.niMonthlyAgorot,
      ownerDrawAgorot: tax.ownerDrawAgorot,
      ownerDrawDay: tax.ownerDrawDay,
      forecastFloorAgorot: tax.forecastFloorAgorot,
    }).then(
      () => toast(he.settings.taxSaved),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  const toggleRule = (type: AlertType, enabled: boolean) => {
    const disabled = new Set(data?.alerts.disabled ?? []);
    if (enabled) disabled.delete(type);
    else disabled.add(type);
    api.updateSettings({ alerts: { disabled: [...disabled] } }).then(
      () => toast(he.settings.alertsSaved),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  const changePassword = () => {
    let current = '';
    let next = '';
    openDialog({
      title: he.settings.changePasswordTitle,
      content: (
        <>
          <div className="field">
            <label htmlFor="pw-current">{he.settings.currentPassword}</label>
            <input id="pw-current" className="input" type="password" autoComplete="current-password" onChange={(event) => (current = event.currentTarget.value)} />
          </div>
          <div className="field">
            <label htmlFor="pw-next">{he.settings.newPassword}</label>
            <input id="pw-next" className="input" type="password" autoComplete="new-password" onChange={(event) => (next = event.currentTarget.value)} />
            <span className="note">{he.settings.newPasswordHint}</span>
          </div>
        </>
      ),
      actions: [
        {
          label: he.settings.changePassword,
          tone: 'primary',
          onClick: () => {
            api.changePassword({ current, next }).then(
              () => toast(he.settings.passwordChanged),
              (error: unknown) => toast(errorMessage(error), { error: true }),
            );
          },
        },
        { label: he.app.cancel },
      ],
    });
  };

  // ── connections ────────────────────────────────────────────────
  const connection = summary?.connections[0];
  const connectionMeta = connection ? CONNECTION_STATUS[connection.status] : null;
  const connections = (
    <>
      <div className="conn card tight" style={{ gap: '8px' }}>
        <div className="ch">
          <h3>
            <ProviderMark providerId={connection?.providerId ?? 'other'} size={28} title={connection ? providerName(connection.providerId) : undefined} />
            {he.settings.financy}
          </h3>
          {connectionMeta ? (
            <Chip tone={connectionMeta.chip as 'good' | 'critical' | 'outline'} icon={connectionMeta.icon}>
              {connectionMeta.label}
            </Chip>
          ) : null}
        </div>
        <dl className="kv">
          <dt>{he.settings.updatedAt}</dt>
          <dd>
            <span className="num">{fmtDateTime(summary?.freshness.lastFetchedAt)}</span>
          </dd>
          <dt>{he.settings.consentUntil}</dt>
          <dd>{connection?.expiryDate ? he.settings.consentUntilValue(fmtDate(connection.expiryDate)) : he.app.notAvailable}</dd>
          {(accounts.data?.items ?? []).map((account) => (
            <Fragment key={account.id}>
              <dt>{ACCOUNT_TYPE[account.type]}</dt>
              <dd>
                {account.name} · <Mask>{account.type === 'CARD' ? `****${account.numberLast4 ?? ''}` : mask(account.numberLast4)}</Mask>
              </dd>
            </Fragment>
          ))}
          <dt>{he.settings.autoFetch}</dt>
          <dd>{data ? he.settings.autoFetchValue(data.sync.everyMinutes) : he.app.notAvailable}</dd>
        </dl>
        <div className="inline">
          <button type="button" className="btn sm" onClick={refresh}>
            <Icon name="refresh" />
            {he.settings.refreshCredits}
          </button>
          {connection?.needsRelink ? (
            <a className="btn sm primary" href="https://app.open-finance.ai" target="_blank" rel="noreferrer noopener">
              <Icon name="external" />
              {he.settings.relink}
            </a>
          ) : null}
        </div>

        <div className="kicker">{he.settings.assign}</div>
        <div className="note">{he.settings.assignNote}</div>
        <div className="rows">
          {(accounts.data?.items ?? []).map((account) => (
            <div className="setting-row" style={{ padding: '7px 0', minHeight: '40px' }} key={account.id}>
              <ProviderMark providerId={account.providerId} size={20} />
              <div className="sd">
                <span>
                  {account.name} <Mask>{account.type === 'CARD' ? `****${account.numberLast4 ?? ''}` : mask(account.numberLast4)}</Mask>
                </span>
                <small>
                  {ACCOUNT_TYPE[account.type]} · {providerName(account.providerId)}
                </small>
              </div>
              <div className="sc">
                <Switch
                  checked={account.assigned}
                  label={he.settings.assignSwitch(account.name)}
                  disabled={summary?.user.role !== 'owner'}
                  onChange={(next) =>
                    api.updateAccount(account.id, { assigned: next }).then(
                      () => toast(he.settings.assignSaved(account.name, next)),
                      (error: unknown) => toast(errorMessage(error), { error: true }),
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="conn card tight" style={{ gap: '8px' }}>
        <div className="ch">
          <h3>
            <Icon name="card" />
            {he.settings.hyp}
          </h3>
          <Chip tone={data?.integrations.hyp.configured ? 'good' : 'outline'} icon={data?.integrations.hyp.configured ? 'check' : 'plug'}>
            {data?.integrations.hyp.configured ? he.settings.connected : he.settings.notConnected}
          </Chip>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="hyp-terminal">{he.settings.hypTerminal}</label>
            <input id="hyp-terminal" className="input ltr" dir="ltr" inputMode="numeric" defaultValue={data?.integrations.hyp.hint ?? ''} />
          </div>
          <div className="field">
            <label htmlFor="hyp-key">{he.settings.hypKey}</label>
            <input id="hyp-key" className="input ltr" dir="ltr" type="password" placeholder="••••••" aria-describedby="hyp-key-h" />
          </div>
        </div>
        <div className="inline">
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              api.testHyp().then(
                (result) => toast(result.message, { error: !result.ok }),
                (error: unknown) => toast(errorMessage(error), { error: true }),
              )
            }
          >
            <Icon name="plug" />
            {he.settings.hypTest}
          </button>
          <span className="note" id="hyp-key-h">
            {he.settings.hypNote}
          </span>
        </div>
        <div className="note">{he.settings.secretsUnsupported}</div>
      </div>

      <div className="conn card tight" style={{ gap: '8px' }}>
        <div className="ch">
          <h3>
            <Icon name="server" />
            {he.settings.labtop}
          </h3>
          <Chip tone="outline" icon="plug">
            {data?.integrations.labtop.configured ? he.settings.connected : he.settings.notConnected}
          </Chip>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="lt-url">{he.settings.labtopUrl}</label>
            <input id="lt-url" className="input ltr" dir="ltr" defaultValue={data?.integrations.labtop.hint ?? ''} />
          </div>
          <div className="field">
            <label htmlFor="lt-token">{he.settings.labtopToken}</label>
            <input id="lt-token" className="input ltr" dir="ltr" type="password" placeholder={he.settings.labtopTokenPlaceholder} />
          </div>
        </div>
        <div className="inline">
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              api.testLabtop().then(
                (result) => toast(result.message, { error: !result.ok }),
                (error: unknown) => toast(errorMessage(error), { error: true }),
              )
            }
          >
            <Icon name="plug" />
            {he.settings.hypTest}
          </button>
          <span className="note">{he.settings.labtopNote}</span>
        </div>
      </div>
    </>
  );

  // ── tax ────────────────────────────────────────────────────────
  const taxBody = tax ? (
    <>
      <div className="form-grid">
        <div className="field">
          <label>{he.settings.businessKind}</label>
          <div className="seg" role="radiogroup" aria-label={he.settings.businessKind}>
            <button type="button" role="radio" aria-checked={tax.businessKind === 'licensed'} disabled>
              <Icon name="lock" className="xs" /> {he.settings.licensed}
            </button>
            <button type="button" role="radio" aria-checked={tax.businessKind === 'exempt'} disabled>
              {he.settings.exempt}
            </button>
          </div>
          <span className="note">{he.settings.lockedHere}</span>
        </div>
        <div className="field">
          <label htmlFor="vat-rate">{he.settings.vatRate}</label>
          <input id="vat-rate" className="input" value={`${Math.round((tax.vatRates[tax.vatRates.length - 1]?.rate ?? 0.18) * 100)}%`} readOnly />
        </div>
        <div className="field">
          <label>{he.settings.vatFrequency}</label>
          <Segmented
            radio
            label={he.settings.vatFrequency}
            value={tax.vatFrequency}
            options={[
              ['bimonthly', he.settings.bimonthly],
              ['monthly', he.settings.monthly],
            ]}
            onChange={(value) => setTax({ ...tax, vatFrequency: value })}
          />
        </div>
        <div className="field">
          <label>{he.settings.vatBasis}</label>
          <Segmented
            radio
            label={he.settings.vatBasis}
            value={tax.vatBasis}
            options={[
              ['cash', 'מזומן'],
              ['accrual', 'מצטבר'],
            ]}
            onChange={(value) => setTax({ ...tax, vatBasis: value })}
          />
          <span className="note">{he.settings.vatBasisNote}</span>
        </div>
        <div className="field">
          <label htmlFor="adv-rate">{he.settings.advanceRate}</label>
          <input
            id="adv-rate"
            className="input"
            inputMode="numeric"
            aria-describedby="adv-h"
            value={String(Math.round(tax.advanceRate * 100))}
            onChange={(event) => setTax({ ...tax, advanceRate: Number(event.currentTarget.value) / 100 })}
          />
          <span className="note" id="adv-h">
            {he.settings.advanceRateNote}
          </span>
        </div>
        <div className="field">
          <label htmlFor="ni">{he.settings.ni}</label>
          <input
            id="ni"
            className="input"
            inputMode="numeric"
            value={String(tax.niMonthlyAgorot / 100)}
            onChange={(event) => setTax({ ...tax, niMonthlyAgorot: Math.round(Number(event.currentTarget.value) * 100) })}
          />
        </div>
        <div className="field">
          <label htmlFor="rday">{he.settings.reportDay}</label>
          <input id="rday" className="input" inputMode="numeric" value={String(tax.reportDay)} onChange={(event) => setTax({ ...tax, reportDay: Number(event.currentTarget.value) })} />
        </div>
      </div>
      <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '4px' }}>{he.settings.forecastGroup}</h3>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="draw">{he.settings.ownerDraw}</label>
          <input
            id="draw"
            className="input"
            inputMode="numeric"
            value={String(tax.ownerDrawAgorot / 100)}
            onChange={(event) => setTax({ ...tax, ownerDrawAgorot: Math.round(Number(event.currentTarget.value) * 100) })}
          />
        </div>
        <div className="field">
          <label htmlFor="drawday">{he.settings.ownerDrawDay}</label>
          <input id="drawday" className="input" inputMode="numeric" value={String(tax.ownerDrawDay)} onChange={(event) => setTax({ ...tax, ownerDrawDay: Number(event.currentTarget.value) })} />
        </div>
        <div className="field">
          <label htmlFor="floor">{he.settings.floor}</label>
          <input
            id="floor"
            className="input"
            inputMode="numeric"
            value={String(tax.forecastFloorAgorot / 100)}
            onChange={(event) => setTax({ ...tax, forecastFloorAgorot: Math.round(Number(event.currentTarget.value) * 100) })}
          />
          <span className="note">{he.settings.floorNote}</span>
        </div>
      </div>
      <div>
        <button type="button" className="btn primary" onClick={saveTax}>
          <Icon name="check" />
          {he.settings.saveTax}
        </button>
      </div>
    </>
  ) : null;

  // ── alerts ─────────────────────────────────────────────────────
  const alertsBody = (
    <>
      <div className="rows">
        {ALERT_ROWS.map((type) => {
          const rule = he.settings.alertRules[type];
          const enabled = !(data?.alerts.disabled ?? []).includes(type);
          return (
            <div className="setting-row" key={type}>
              <div className="sd">
                <span>{rule.label}</span>
                <small>{rule.hint(ruleValues)}</small>
              </div>
              <div className="sc">
                <Switch checked={enabled} label={rule.label} onChange={(next) => toggleRule(type, next)} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="note">{he.settings.alertsNote}</div>
    </>
  );

  // ── display ────────────────────────────────────────────────────
  const displayBody = (
    <div className="rows">
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.theme}</span>
          <small>{he.settings.themeNote}</small>
        </div>
        <div className="sc">
          <Segmented
            radio
            label={he.settings.theme}
            value={appearance.theme}
            options={[
              ['light', he.settings.themeLight, 'sun'],
              ['dark', he.settings.themeDark, 'moon'],
              ['auto', he.settings.themeAuto, 'laptop'],
            ]}
            onChange={(value) => setAppearance({ theme: value })}
          />
        </div>
      </div>
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.density}</span>
          <small>{he.settings.densityNote}</small>
        </div>
        <div className="sc">
          <Segmented
            radio
            label={he.settings.density}
            value={appearance.density}
            options={[
              ['normal', he.settings.densityNormal],
              ['compact', he.settings.densityCompact],
            ]}
            onChange={(value) => setAppearance({ density: value })}
          />
        </div>
      </div>
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.numberLocale}</span>
          <small>{he.settings.numberLocaleNote}</small>
        </div>
        <div className="sc">
          <Chip tone="outline">
            <span className="ltr">he-IL</span> · <Money agorot={123450} decimals={2} />
          </Chip>
        </div>
      </div>
    </div>
  );

  // ── account ────────────────────────────────────────────────────
  const accountBody = (
    <div className="rows">
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.email}</span>
          <small>
            <span className="ltr">{user?.email}</span>
          </small>
        </div>
        <div className="sc">
          <button type="button" className="btn sm" onClick={changePassword}>
            <Icon name="key" />
            {he.settings.changePassword}
          </button>
        </div>
      </div>
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.devices}</span>
        </div>
        <div className="sc">
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              api.logoutAll().then(
                () => toast(he.settings.logoutAllDone),
                (error: unknown) => toast(errorMessage(error), { error: true }),
              )
            }
          >
            <Icon name="logout" />
            {he.settings.logoutAll}
          </button>
        </div>
      </div>
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.exportAll}</span>
          <small>{he.settings.exportAllNote}</small>
        </div>
        <div className="sc">
          <button type="button" className="btn sm" onClick={() => toast(he.app.fullVersion)}>
            <Icon name="download" />
            {he.settings.exportAllBtn}
          </button>
        </div>
      </div>
      <div className="setting-row">
        <div className="sd">
          <span>{he.settings.logout}</span>
          <small>{he.settings.logoutNote}</small>
        </div>
        <div className="sc">
          <button
            type="button"
            className="btn sm danger"
            onClick={() => {
              void api.logout().catch(() => undefined);
              navigate(buildPath('login'));
            }}
          >
            <Icon name="logout" />
            {he.settings.logout}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="settings">
      <nav className="tabs-v" aria-label={he.settings.tabsAria} role="tablist" aria-orientation="vertical">
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className="tab-v"
            role="tab"
            id={`tab-${tab}`}
            aria-controls={`sec-${tab}`}
            aria-selected={active === tab}
            tabIndex={active === tab ? 0 : -1}
            onClick={() => navigate(buildPath('settings', tab))}
          >
            <Icon name={TAB_ICONS[tab]} />
            {he.settings.tabs[tab]}
          </button>
        ))}
      </nav>
      <div className="stack" style={{ gap: '12px' }}>
        {section('connections', he.settings.tabs.connections, connections)}
        {section('tax', he.settings.tabs.tax, taxBody)}
        {section('alerts', he.settings.tabs.alerts, alertsBody)}
        {section('display', he.settings.tabs.display, displayBody)}
        {section('account', he.settings.tabs.account, accountBody)}
      </div>
    </div>
  );
}
